// @flow
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Done as DoneIcon,
  DoneAll as DoneAllIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon
} from '@material-ui/icons';
import DateFnsUtils from '@date-io/date-fns';
import {
  DatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import { Autocomplete } from '@material-ui/lab';
import { Formik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { countries } from '../data/countries';
import Country from '../model/Country';
import SignProcess from '../model/SignProcess';
import { toSentence } from '../util/string';
import { isNotValidEmail, isNotValidName, isNotValidPhone } from '../util/validator';

const useStyles = makeStyles((theme) => ({
  draft: {
    color: theme.palette.secondary[ '900' ],
  },
  grey: {
    color: theme.palette.text.secondary,
  },
  error: {
    color: theme.palette.error.main,
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  form: {
    width: '100%', // Fix IE 11 issue.
  },
}));

type Props = {
  code: string,
  process: SignProcess,
  expanded: boolean,
  disabled: boolean,
  onExpanded: (string) => (event: any, isExpanded: boolean) => void,
  onSave: (SignProcess) => void,
};

const ProcessPersonalData = (props: Props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { code, process, expanded, disabled, onExpanded, onSave } = props;

  const validate = values => {
    const errors = {};
    if (!values.name) {
      errors.name = t('Required');
    } else if (isNotValidName(values.name)) {
      errors.name = t('Invalid name');
    }
    if (!values.lastname) {
      errors.lastname = t('Required');
    } else if (isNotValidName(values.lastname)) {
      errors.lastname = t('Invalid lastname');
    }
    if (!values.email) {
      errors.email = t('Required');
    } else if (isNotValidEmail(values.email)) {
      errors.email = t('Invalid email address');
    }
    if (!values.phone) {
      errors.phone = t('Required');
    } else if (isNotValidPhone(values.phone)) {
      errors.phone = t('Invalid phone');
    }
    return errors;
  };

  const onSubmit = async (values, { setSubmitting }) => {
    let data = process;
    data.targets = Object.values(values.targets);
    data.name = values.name;
    data.lastname = values.lastname;
    data.email = values.email;
    data.phone = values.phone;
    data.legalIDType = values.legalIDType;
    data.legalIDExpiresAt = values.legalIDExpiresAt;
    data.legalID = values.legalID;
    data.idDocumentCode = values.idDocumentCode;
    data.countryOfResidence = values.countryOfResidence ? values.countryOfResidence.label : '';
    data.provinceOfResidence = values.provinceOfResidence;
    data.cityOfResidence = values.cityOfResidence;
    data.addressOfResidence = values.addressOfResidence;
    data.zipCode = values.zipCode;
    data.forInvoicing = !!values.forInvoicing;
    data.tributaryName = values.tributaryName;
    data.tributaryLegalID = values.tributaryLegalID;
    data.gender = values.gender;
    data.birthday = values.birthday;
    data.countryOfBirth = values.countryOfBirth ? values.countryOfBirth.label : '';
    onSave(data);
    setSubmitting(false);
  };

  let processStatusText = process.hasDataLoaded() ? 'Completed' : 'Not completed';
  let processStatusIcon = process.hasDataLoaded() ? <DoneIcon color="action"/> : <EditIcon color="secondary"/>;
  if (process.hasDataValidated()) {
    processStatusText = 'Validated';
    processStatusIcon = <DoneAllIcon color="primary"/>;
  }

  const handleIDChange = (e, name, values, handleChange, setFieldValue) => {
    handleChange(e);
    if (name.indexOf('signTarget') === 0) {
      if (!!values.targets[name]) {
        values.targets[name].value = e.currentTarget.checked;
      }
      setFieldValue('targets', values.targets);
    }
    const legalID = name === 'legalID'
      ? e.currentTarget.value
      : values.legalID;
    const tributaryLegalID = name === 'tributaryLegalID'
      ? e.currentTarget.dataset.value
      : values.tributaryLegalID;
    const legalIDType = name === 'legalIDType'
      ? e.currentTarget.dataset.value
      : values.legalIDType;
    const forInvoicing = name === 'forInvoicing'
      ? e.currentTarget.checked
      : values.forInvoicing;
    if ('EC_CEDULA' === legalIDType
        && forInvoicing
        && legalID.length === 10) {
      setFieldValue('tributaryLegalID', legalID + '001');
    } else if ('EC_RUC' === legalIDType
               && forInvoicing
               && legalID.length === 13
               && !tributaryLegalID) {
      setFieldValue('tributaryLegalID', legalID);
    }
  };

  const processTargets = {};
  if (process.targets) {
    process.targets.forEach(signTarget => processTargets[signTarget.code] = signTarget);
  }

  return (
    <Formik
      initialValues={{
        targets: processTargets,
        name: process.name,
        lastname: process.lastname,
        email: process.email,
        phone: process.phone,
        legalIDType: process.legalIDType || '',
        legalID: process.legalID || '',
        legalIDExpiresAt: process.legalIDExpiresAt,
        idDocumentCode: process.idDocumentCode || '',
        countryOfResidence: Country.find(countries, process.countryOfResidence),
        provinceOfResidence: process.provinceOfResidence || '',
        cityOfResidence: process.cityOfResidence || '',
        addressOfResidence: process.addressOfResidence || '',
        zipCode: process.zipCode || '',
        forInvoicing: !!process.forInvoicing,
        tributaryName: process.tributaryName || '',
        tributaryLegalID: process.tributaryLegalID || '',
        gender: process.gender,
        birthday: process.birthday,
        countryOfBirth: Country.find(countries, process.countryOfBirth),
      }}
      validate={validate}
      onSubmit={onSubmit}
    >
      {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
        }) => (
        <form className={classes.form} onSubmit={handleSubmit}>
          <Accordion
            key={code}
            expanded={expanded}
            onChange={onExpanded(code)}
            TransitionProps={{ unmountOnExit: true }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon/>}
              aria-controls={`${code}-panel`}
              id={`${code}-panel`}
            >
              {processStatusIcon}&nbsp;
              <Typography className={classes.heading}>
                {t('Personal data')}
              </Typography>
              <Typography className={classes.secondaryHeading}>
                {t(processStatusText)}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={1}>
                {Object.values(values.targets).map(signTarget => (
                  <Grid key={signTarget.code} item xs={12} sm={12} md={12} lg={12}
                        style={{marginBottom: 5}}>
                    <FormControlLabel
                      control={<Checkbox
                        disabled={disabled}
                        checked={signTarget.value}
                        onChange={(e) => handleIDChange(e, signTarget.code, values, handleChange, setFieldValue)}
                        onBlur={handleBlur}
                        name={signTarget.code}
                        color="primary"/>}
                      label={<strong>
                        {signTarget.text}
                      </strong>}/>
                  </Grid>
                ))}
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    disabled={disabled}
                    autoComplete="name"
                    name="name"
                    variant="outlined"
                    required
                    fullWidth
                    id="name"
                    label={t('Name')}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.name}
                  />
                  <span className={classes.error}>
                    {errors.name && touched.name && errors.name}
                  </span>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    disabled={disabled}
                    autoComplete="lastname"
                    name="lastname"
                    variant="outlined"
                    required
                    fullWidth
                    id="lastname"
                    label={t('Lastname')}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.lastname}
                  />
                  <span className={classes.error}>
                    {errors.lastname && touched.lastname && errors.lastname}
                  </span>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    disabled={disabled}
                    variant="outlined"
                    required
                    fullWidth
                    id="email"
                    label={t('Email')}
                    name="email"
                    autoComplete="email"
                    type="email"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                  />
                  <span className={classes.error}>
                    {errors.email && touched.email && errors.email}
                  </span>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    disabled={disabled}
                    variant="outlined"
                    required
                    fullWidth
                    id="phone"
                    label={t('Phone')}
                    name="phone"
                    autoComplete="phone"
                    type="tel"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.phone}
                  />
                  <span className={classes.error}>
                    {errors.phone && touched.phone && errors.phone}
                  </span>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    disabled={disabled}
                  >
                    <InputLabel id="legalIDTypeLabel">
                      {t('Legal ID type')}
                    </InputLabel>
                    <Select
                      labelId="legalIDTypeLabel"
                      label={t('Legal ID type')}
                      id="legalIDType"
                      name="legalIDType"
                      value={values.legalIDType}
                      onChange={(e) => handleIDChange(e, 'legalIDType', values, handleChange, setFieldValue)}
                    >
                      {process.validLegalIDTypes.map((item) => (
                        <MenuItem key={item} value={item}>
                          {t(toSentence(item))}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <span className={classes.error}>
                    {errors.legalIDType && touched.legalIDType && errors.legalIDType}
                  </span>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    disabled={disabled}
                    variant="outlined"
                    fullWidth
                    id="legalID"
                    label={t('Legal ID')}
                    name="legalID"
                    autoComplete="legalID"
                    onChange={(e) => handleIDChange(e, 'legalID', values, handleChange, setFieldValue)}
                    onBlur={handleBlur}
                    value={values.legalID}
                  />
                  <span className={classes.error}>
                    {errors.legalID && touched.legalID && errors.legalID}
                  </span>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <DatePicker
                      disabled={disabled}
                      inputVariant="outlined"
                      fullWidth
                      id="legalIDExpiresAt"
                      label={t('Legal ID expires at')}
                      name="legalIDExpiresAt"
                      onChange={(value) => {
                        setFieldValue('legalIDExpiresAt', value);
                      }}
                      onBlur={handleBlur}
                      value={values.legalIDExpiresAt}
                      format="dd/MM/yyyy"
                      KeyboardButtonProps={{
                        'aria-label': t('Change legal ID expiration'),
                      }} />
                  </MuiPickersUtilsProvider>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    disabled={disabled}
                    variant="outlined"
                    fullWidth
                    id="idDocumentCode"
                    label={t('ID Document Code')}
                    name="idDocumentCode"
                    autoComplete="idDocumentCode"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.idDocumentCode}
                  />
                  <span className={classes.error}>
                    {errors.idDocumentCode && touched.idDocumentCode && errors.idDocumentCode}
                  </span>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <Autocomplete
                    disabled={disabled}
                    fullWidth
                    id="countryOfResidence"
                    name="countryOfResidence"
                    options={countries}
                    autoHighlight
                    getOptionLabel={(option) => option.label}
                    value={values.countryOfResidence}
                    renderOption={(option) => (
                      <><span>{option.flag()}</span>&nbsp;{option.label}</>
                    )}
                    onChange={(e, value) => {
                      setFieldValue('countryOfResidence', value);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('Country of residence')}
                        variant="outlined"
                        onBlur={handleBlur}
                        inputProps={{
                          ...params.inputProps,
                          autoComplete: 'nothing-at-all', // disable autocomplete and autofill
                        }}
                      />
                    )}
                  />
                  <span className={classes.error}>
                    {errors.countryOfResidence && touched.countryOfResidence && errors.countryOfResidence}
                  </span>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    disabled={disabled}
                    variant="outlined"
                    fullWidth
                    id="provinceOfResidence"
                    label={t('Province of residence')}
                    name="provinceOfResidence"
                    autoComplete="provinceOfResidence"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.provinceOfResidence}
                  />
                  <span className={classes.error}>
                    {errors.provinceOfResidence && touched.provinceOfResidence && errors.provinceOfResidence}
                  </span>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    disabled={disabled}
                    variant="outlined"
                    fullWidth
                    id="cityOfResidence"
                    label={t('City of residence')}
                    name="cityOfResidence"
                    autoComplete="cityOfResidence"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.cityOfResidence}
                  />
                  <span className={classes.error}>
                    {errors.cityOfResidence && touched.cityOfResidence && errors.cityOfResidence}
                  </span>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    disabled={disabled}
                    variant="outlined"
                    fullWidth
                    id="addressOfResidence"
                    label={t('Address of residence')}
                    name="addressOfResidence"
                    autoComplete="addressOfResidence"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.addressOfResidence}
                  />
                  <span className={classes.error}>
                    {errors.addressOfResidence && touched.addressOfResidence && errors.addressOfResidence}
                  </span>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    disabled={disabled}
                    variant="outlined"
                    fullWidth
                    id="zipCode"
                    label={t('Zip code')}
                    name="zipCode"
                    autoComplete="zipCode"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.zipCode}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    disabled={disabled}
                  >
                    <InputLabel id="genderLabel">
                      {t('Gender')}
                    </InputLabel>
                    <Select
                      labelId="genderLabel"
                      label={t('Gender')}
                      id="gender"
                      name="gender"
                      value={values.gender}
                      onChange={handleChange}
                    >
                      {process.genders.map((item) => (
                        <MenuItem key={item} value={item}>
                          {t('Gender ' + item)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <span className={classes.error}>
                    {errors.gender && touched.gender && errors.gender}
                  </span>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <DatePicker
                      disabled={disabled}
                      inputVariant="outlined"
                      fullWidth
                      id="birthday"
                      label={t('Birthday')}
                      name="birthday"
                      onChange={(value) => {
                        setFieldValue('birthday', value);
                      }}
                      onBlur={handleBlur}
                      value={values.birthday}
                      format="dd/MM/yyyy"
                      KeyboardButtonProps={{
                        'aria-label': t('Change birthday'),
                      }} />
                  </MuiPickersUtilsProvider>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <Autocomplete
                    disabled={disabled}
                    fullWidth
                    id="countryOfBirth"
                    name="countryOfBirth"
                    options={countries}
                    autoHighlight
                    getOptionLabel={(option) => option.label}
                    value={values.countryOfBirth}
                    renderOption={(option) => (
                      <><span>{option.flag()}</span>&nbsp;{option.label}</>
                    )}
                    onChange={(e, value) => {
                      setFieldValue('countryOfBirth', value);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('Country of birth')}
                        variant="outlined"
                        onBlur={handleBlur}
                        inputProps={{
                          ...params.inputProps,
                          autoComplete: 'nothing-at-all', // disable autocomplete and autofill
                        }}
                      />
                    )}
                  />
                  <span className={classes.error}>
                    {errors.countryOfBirth && touched.countryOfBirth && errors.countryOfBirth}
                  </span>
                </Grid>
                {false && <Grid item xs={12} sm={12} md={6} lg={4}>
                  <FormControlLabel
                    control={<Checkbox
                      disabled={disabled}
                      checked={values.forInvoicing}
                      onChange={(e) => handleIDChange(e, 'forInvoicing', values, handleChange, setFieldValue)}
                      onBlur={handleBlur}
                      name="forInvoicing"
                      color="primary"/>}
                    label={<strong>
                      {t('For invoicing description')}
                    </strong>}/>
                </Grid>}
                {process.canCreateInvoice() && values.forInvoicing && <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    disabled={disabled}
                    variant="outlined"
                    fullWidth
                    id="tributaryName"
                    label={t('Tributary Name')}
                    name="tributaryName"
                    autoComplete="tributaryName"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.tributaryName}
                  />
                  <span className={classes.error}>
                    {errors.tributaryName && touched.tributaryName && errors.tributaryName}
                  </span>
                </Grid>}
                {process.canCreateInvoice() && values.forInvoicing && <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    disabled={disabled || ('EC_CEDULA' === values.legalIDType && values.forInvoicing)}
                    variant="outlined"
                    fullWidth
                    id="tributaryLegalID"
                    label={t('Tributary Legal ID')}
                    name="tributaryLegalID"
                    autoComplete="tributaryLegalID"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.tributaryLegalID}
                  />
                  <span className={classes.error}>
                    {errors.tributaryLegalID && touched.tributaryLegalID && errors.tributaryLegalID}
                  </span>
                </Grid>}
              </Grid>
            </AccordionDetails>
            {!process.hasDocumentsValidated() && <>
              <Divider/>
              <AccordionActions>
                <Button
                  type="submit"
                  disabled={disabled}
                  variant="contained"
                  color="primary"
                  startIcon={disabled ? <CircularProgress color="secondary" size="1.2em"/> : null}
                >
                  {t('Save')}
                </Button>
              </AccordionActions>
            </>}
          </Accordion>
        </form>
      )}
    </Formik>
  );
};

export default ProcessPersonalData;
